// src/components/home/UpcomingScheduleCard.tsx
//
// Next ~7 days of calendar events. Reuses the existing /events API — the CRM is
// the canonical appointment store (Scheduler-app bookings land here too), so no
// new backend work is required to show "what's coming up".

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetEventsQuery } from '../../api/eventsApi';

function rangeFrom(today: string): { from: string; to: string } {
  const start = new Date(`${today}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return { from: start.toISOString(), to: end.toISOString() };
}

function formatWhen(startAt: string, isAllDay: boolean): string {
  const d = new Date(startAt);
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  if (isAllDay) return `${day} · All day`;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

interface UpcomingScheduleCardProps {
  workspaceId: string;
  today: string;
}

export default function UpcomingScheduleCard({ workspaceId, today }: UpcomingScheduleCardProps) {
  const navigate = useNavigate();
  const { from, to } = useMemo(() => rangeFrom(today), [today]);

  const { data: events, isLoading } = useGetEventsQuery(
    { workspaceId, from, to },
    { skip: !workspaceId },
  );

  const upcoming = useMemo(
    () => [...(events ?? [])].sort((a, b) => a.startAt.localeCompare(b.startAt)).slice(0, 5),
    [events],
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Upcoming Schedule</h2>
        <button onClick={() => navigate('/schedule')} className="text-xs text-blue-600 hover:underline">
          View calendar
        </button>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : upcoming.length === 0 ? (
        <p className="text-sm text-gray-500">No events in the next 7 days.</p>
      ) : (
        <ul className="space-y-2">
          {upcoming.map((ev) => (
            <li key={ev.id}>
              <button
                onClick={() => navigate(`/schedule?eventId=${ev.id}`)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-1 self-stretch rounded-full bg-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{ev.title}</p>
                  <p className="text-xs text-gray-500">{formatWhen(ev.startAt, ev.isAllDay)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
